import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./SsnCardVerification.module.scss";
import { useSsnCardVerification } from "../../hooks/ssnCardVerification";
import { useConsumer } from "../ConsumerProvider";
import { getConsumerConfig } from "../../consumers";
import { InfoScreen } from "./screens/InfoScreen";
import { CaptureScreen as CaptureScreenAuto } from "./screens/auto/CaptureScreen";
import { CaptureScreen as CaptureScreenManual } from "./screens/manual/CaptureScreen";
import { ProcessingScreen } from "./screens/ProcessingScreen";
import { ConfirmationScreen } from "./screens/ConfirmationScreen";
import { ErrorScreen } from "./screens/ErrorScreen";
import { LanguageSelector } from "../faceVerification/LanguageSelector";
import { initAnalytics, setAnalyticsLocale, reportAnalyticsSetupFailure } from "../../shared/analytics/analyticsClient";
import { logger } from "../../shared/utils/logger";

/**
 * SsnCardVerification — iframe-side view for the standalone SSN card SDK.
 *
 * A pure view over the `useSsnCardVerification` controller : it reads
 * `state` and renders the matching screen, and calls the hook's intent actions
 * on user input. It never sets state itself — the hook owns every transition.
 *
 * Capture sequences front then back inside one `<document-reader>`, so there is
 * one `capturing` state — CaptureScreen owns the side-to-side transition.
 *
 * States: initializing -> ready -> capturing -> processing -> confirming? -> completed | error.
 * The `confirming` state only happens when the host opted in via
 * `init({ confirmationScreenEnabled: true })` (VEX-7846) — otherwise a passed
 * outcome completes immediately, same as before that ticket.
 */
export function SsnCardVerification() {
  const { t, i18n } = useTranslation();
  const { consumer, fragnoLogoEnabled } = useConsumer();
  const consumerConfig = getConsumerConfig(consumer);
  // VEX-7983: with the Fragno logo on, BrandLayout's header carries the branding,
  // so the cards lose their own consumer logo header.
  const consumerLogo = fragnoLogoEnabled ? undefined : consumerConfig?.logo;
  const themeClass = consumerConfig?.themeClass;
  const [showSpinner, setShowSpinner] = useState(true);

  const { state, error, verificationResult, initialize, beginCapture, submitImages, confirmVerification, handleVerificationError, manualCaptureEnabled, outOfRetakes } = useSsnCardVerification();

  // Analytics transport + session for this launch (phase 1; identity is bound later at
  // IDME_INIT). In render, not an effect: effects run child-first, so from an effect
  // here a screen's mount-time `Viewed` would emit before the transport exists. A
  // parent renders before every child, so this ordering holds by construction. The
  // ref only skips ordinary re-renders; StrictMode's mount double-render gets a fresh
  // one and runs this again, which is safe because initAnalytics is idempotent.
  const analyticsStarted = useRef(false);
  if (!analyticsStarted.current) {
    analyticsStarted.current = true;
    // Analytics setup must never block the core flow — if init throws, swallow it
    // so the parent handshake still runs (honors "a failure never blocks the flow").
    try {
      initAnalytics({ module: "ssn_card", provider: "regula", locale: i18n.language });
    } catch (err) {
      logger.warn("[SsnCardVerification] analytics init failed; continuing without analytics", err);
      reportAnalyticsSetupFailure("init_failed", { module: "ssn_card", error: err instanceof Error ? err.message : String(err) });
    }
  }

  useEffect(() => {
    const cleanup = initialize();
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The member can switch language mid-flow via the footer.
  useEffect(() => {
    setAnalyticsLocale(i18n.language);
  }, [i18n.language]);

  // Toggle the spinner animation on the processing screen.
  const handleHideAnimation = () => setShowSpinner((prev) => !prev);

  // Handler: user indicates on the info screen that they don't have an SSN card.
  const handleNoId = () => {
    handleVerificationError("user_no_ssn_card", "User indicated they do not have a valid Social Security card", undefined, {});
  };

  // Handler: user clicks "Retake photo"/"Join a video call" on the confirmation
  // screen. Sends retake request to parent — IVA owns retry limits and will
  // restart the flow if allowed (mirrors legal-id's handleRetake). Reports a
  // distinct errorCode once outOfRetakes is true (VEX-7961), so IVA doesn't have
  // to infer the screen's state from its own retry-limit bookkeeping — same
  // click, same callback, just an honest label for what IVA already told us.
  const handleRetake = () => {
    const errorCode = outOfRetakes ? "retake_limit_reached" : "user_retake_requested";
    const message = outOfRetakes
      ? "User requested to retake photo of SSN card after exhausting retakes"
      : "User requested to retake photo of SSN card";
    handleVerificationError(errorCode, message, undefined, { uuid: verificationResult?.data?.uuid });
  };

  // Consumer partner logo header shown inside non-info cards (mirrors legal-id).
  const consumerLogoHeader = consumerLogo ? (
    <div className={styles.cardConsumerLogoHeader}>
      <img src={consumerLogo.src} alt={consumerLogo.alt} />
      <div className={styles.cardConsumerLogoDivider} />
    </div>
  ) : null;

  return (
    <div className={[styles.pageContainer, themeClass].filter(Boolean).join(' ')}>
      <div className={styles.container}>
        <div className={styles.contentContainer}>

          {/* IDMe logo — shown only when there's no consumer branding */}
          {!fragnoLogoEnabled && !consumerLogo && (
            <div className={styles.logoContainer}>
              <div className={styles.logo}>
                <img src="/assets/svg/idme-logo.svg" alt={t('ssnCard.logo.alt')} />
              </div>
            </div>
          )}

          {/* Initializing — spinner while the parent handshake completes */}
          {state === "initializing" && (
            <div className={styles.card}>
              {consumerLogoHeader}
              <div className={styles.spinner} />
            </div>
          )}

          {/* Ready — info screen with the "Open camera" button */}
          {state === "ready" && (
            <InfoScreen onOpenCamera={beginCapture} onNoId={handleNoId} consumerLogo={consumerLogo} />
          )}

          {/* Capturing — Regula reader; submits front + back to the hook. Maps the
              screen's (code, message, feedback) onto the hook's error handler so the
              capture `feedback` lands in the right slot (not statusCode).
              `manualCaptureEnabled` (from init({ manualCaptureEnabled }), via IDME_INIT)
              picks the shutter-gated manual flow vs. the default auto-capture flow —
              see regula/auto and regula/manual, kept as independent, separately
              deletable implementations for the duration of this experiment. */}
          {state === "capturing" && (
            manualCaptureEnabled ? (
              <CaptureScreenManual
                onSubmit={submitImages}
                onError={(code, message, feedback) =>
                  handleVerificationError(code, message, undefined, undefined, undefined, feedback)
                }
              />
            ) : (
              <CaptureScreenAuto
                onSubmit={submitImages}
                onError={(code, message, feedback) =>
                  handleVerificationError(code, message, undefined, undefined, undefined, feedback)
                }
              />
            )
          )}

          {/* Processing — polling the backend for the outcome */}
          {state === "processing" && (
            <ProcessingScreen showSpinner={showSpinner} onHideAnimation={handleHideAnimation} />
          )}

          {/* Confirming — review extracted name/SSN before proceeding (VEX-7846) */}
          {state === "confirming" && verificationResult && (
            <ConfirmationScreen
              verificationResult={verificationResult}
              onConfirm={confirmVerification}
              onRetake={handleRetake}
              consumerLogo={consumerLogo}
              outOfRetakes={outOfRetakes}
            />
          )}

          {/* Completed — brief success state */}
          {state === "completed" && (
            <div className={styles.card}>
              {consumerLogoHeader}
              <div className={styles.successIcon} aria-hidden="true">✓</div>
              <h2 className={styles.title}>{t('ssnCard.completed.title')}</h2>
              <p className={styles.subtitle}>{t('ssnCard.completed.subtitle')}</p>
            </div>
          )}

          {/* Error — terminal failure */}
          {state === "error" && <ErrorScreen message={error} consumerLogo={consumerLogo} />}

        </div>

        {/* Hidden during capture: the reader is a fixed full-viewport overlay, so a
            rendered footer would be invisible but still tabbable underneath. */}
        {state !== "capturing" && (
          <footer className={styles.footer}>
            <LanguageSelector />
            <div className={styles.footerLinks}>
              <a href="https://www.id.me/about" target="_blank" rel="noopener noreferrer" className={styles.footerLink}>{t('ssnCard.footer.whatIsIdme')}</a>
              <span aria-hidden="true" className={styles.footerDivider}>|</span>
              <a href="https://www.id.me/terms" target="_blank" rel="noopener noreferrer" className={styles.footerLink}>{t('ssnCard.footer.termsOfService')}</a>
              <span aria-hidden="true" className={styles.footerDivider}>|</span>
              <a href="https://www.id.me/privacy" target="_blank" rel="noopener noreferrer" className={styles.footerLink}>{t('ssnCard.footer.privacyPolicy')}</a>
            </div>
          </footer>
        )}
      </div>
    </div>
  );
}
